import { useCallback, useEffect, useState } from "react";

import type { Stripe, StripeElements } from "@stripe/stripe-js";

import {
  useCreatePolicyStripePaymentMethodSetupIntentMutation,
  useGetPolicyWithPaymentMethodsQuery,
  useRetryRenewalFailedPaymentMutation,
} from "@covertree/agent-portal-graphql";
import { Toast } from "@covertree/utils";
import { getErrorMessageFromApolloError } from "@utils/getErrorMessageFromApolloError";
import { usePolicyDetails } from "../../../../../PolicyDetailsContext";

const newPaymentMethodErrorMessage =
  "Could not add a new payment method. Please try again.";

export interface SubmitStripePaymentMethodParams {
  stripe: Stripe;
  elements: StripeElements;
}

export function useAddNewStripePaymentMethod(
  policyLocator: string,
  shouldTriggerRenewalReissuance: boolean
) {
  const [isLoading, setIsLoading] = useState(false);
  const [isAddNewPaymentMethodModeOpen, setAddNewPaymentMethodModeOpen] =
    useState(false);
  const [newPaymentMethodId, setNewPaymentMethodId] = useState<string>();
  const [renewalRetrySuccess, setRenewalRetrySuccess] =
    useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string>();

  const {
    policy: { policyholder },
    refetchPolicy,
  } = usePolicyDetails();

  const { data: getPolicyData } = useGetPolicyWithPaymentMethodsQuery({
    variables: { policyLocator },
    skip: !newPaymentMethodId,
    pollInterval: 1000,
    nextFetchPolicy: "network-only",
  });

  const [createPolicyStripePaymentMethodSetupIntent] =
    useCreatePolicyStripePaymentMethodSetupIntentMutation();

  const [retryRenewalFailedPayment] = useRetryRenewalFailedPaymentMutation();

  const fetchClientSecret = useCallback(async () => {
    try {
      const result = await createPolicyStripePaymentMethodSetupIntent({
        variables: { policyLocator },
      });

      if (!result?.data?.createPolicyStripePaymentMethodSetupIntent) {
        throw new Error(newPaymentMethodErrorMessage);
      }

      setClientSecret(
        result.data.createPolicyStripePaymentMethodSetupIntent.clientSecret
      );
    } catch (e) {
      Toast.showError(
        getErrorMessageFromApolloError(e) || newPaymentMethodErrorMessage,
        8_000
      );
    }
  }, [policyLocator, createPolicyStripePaymentMethodSetupIntent]);

  useEffect(() => {
    if (isAddNewPaymentMethodModeOpen) {
      fetchClientSecret();
    } else {
      setClientSecret(undefined);
    }
  }, [isAddNewPaymentMethodModeOpen]);

  const handleRetryRenewalFailedPayment =
    useCallback(async (): Promise<boolean> => {
      try {
        await retryRenewalFailedPayment({
          variables: { policyLocator },
        });
        return true;
      } catch (e) {
        Toast.showError(
          getErrorMessageFromApolloError(e) ||
            "Renewal issuance failed. For assistance, visit the 'Contact Us' section."
        );
        return false;
      }
    }, [policyLocator, retryRenewalFailedPayment]);

  const onAddNewPaymentMethod = useCallback(
    async ({ stripe, elements }: SubmitStripePaymentMethodParams) => {
      try {
        setIsLoading(true);

        if (!clientSecret) {
          throw new Error(newPaymentMethodErrorMessage);
        }

        const { error: submitError } = await elements.submit();

        if (submitError) {
          throw new Error(submitError.message || newPaymentMethodErrorMessage);
        }

        const unitNumber = !!policyholder.mailingAddress.unitNumber
          ? ` #${policyholder.mailingAddress.unitNumber}`
          : "";

        const stripeResult = await stripe.confirmSetup({
          clientSecret,
          elements,
          redirect: "if_required",
          confirmParams: {
            payment_method_data: {
              billing_details: {
                name: `${policyholder.firstName} ${policyholder.lastName}`,
                email: policyholder.emailAddress,
                address: {
                  country: "US",
                  postal_code: policyholder.mailingAddress.zipCode,
                  state: policyholder.mailingAddress.state,
                  city: policyholder.mailingAddress.city,
                  line1: `${policyholder.mailingAddress.streetAddress}${unitNumber}`,
                },
              },
            },
            return_url: "https://covertree.com",
          },
        });

        if (stripeResult.error) {
          throw new Error(
            stripeResult.error.message || newPaymentMethodErrorMessage
          );
        }

        const setupIntent = stripeResult.setupIntent;

        if (!setupIntent?.payment_method) {
          throw new Error(newPaymentMethodErrorMessage);
        }

        // For us_bank_account, setupIntent status might be "processing" which is acceptable
        if (
          setupIntent.status !== "succeeded" &&
          setupIntent.status !== "processing"
        ) {
          throw new Error(newPaymentMethodErrorMessage);
        }

        if (shouldTriggerRenewalReissuance) {
          const renewalRetryResult = await handleRetryRenewalFailedPayment();
          setRenewalRetrySuccess(renewalRetryResult);
        }

        if (typeof setupIntent.payment_method === "string") {
          setNewPaymentMethodId(setupIntent.payment_method);
        } else {
          setNewPaymentMethodId(setupIntent.payment_method.id);
        }
      } catch (e) {
        Toast.showError(
          getErrorMessageFromApolloError(e) ||
            (e instanceof Error ? e.message : newPaymentMethodErrorMessage),
          8_000
        );
        setIsLoading(false);
      }
    },
    [
      clientSecret,
      shouldTriggerRenewalReissuance,
      handleRetryRenewalFailedPayment,
      policyholder,
    ]
  );

  useEffect(() => {
    if (newPaymentMethodId && getPolicyData?.getPolicy?.paymentMethods) {
      const allMethods = [
        ...(getPolicyData.getPolicy.paymentMethods.creditCardPaymentMethods ||
          []),
        ...(getPolicyData.getPolicy.paymentMethods.achPaymentMethods || []),
      ];

      const newPaymentMethod = allMethods.find(
        ({ id }) => id === newPaymentMethodId
      );

      if (newPaymentMethod?.default) {
        setIsLoading(false);
        setNewPaymentMethodId(undefined);
        setAddNewPaymentMethodModeOpen(false);
        refetchPolicy();

        if (shouldTriggerRenewalReissuance && renewalRetrySuccess) {
          Toast.showSuccess(
            "Payment details have been updated successfully, and the renewal issuance has been completed."
          );
        }
      }
    }
  }, [
    newPaymentMethodId,
    getPolicyData?.getPolicy?.paymentMethods?.creditCardPaymentMethods,
    getPolicyData?.getPolicy?.paymentMethods?.achPaymentMethods,
    refetchPolicy,
    shouldTriggerRenewalReissuance,
    renewalRetrySuccess,
  ]);

  return {
    isAddNewPaymentMethodModeOpen,
    setAddNewPaymentMethodModeOpen,
    onAddNewPaymentMethod,
    loading: isLoading,
    clientSecret,
  };
}
